import { useCallback, useRef, useState } from "react";
import type { SignWord } from "../components/SignPlayer";
import { getIdToken, isAuthBypassed, refreshTokens } from "../lib/auth";

export type SignConversionStage =
  | "tokenizing"
  | "generating_synonyms"
  | "searching_dictionary"
  | "assembling";

export const SIGN_CONVERSION_STAGE_LABELS: Record<SignConversionStage, string> =
  {
    tokenizing: "文章を解析しています...",
    generating_synonyms: "類語を探しています...",
    searching_dictionary: "辞書を検索しています...",
    assembling: "手話の順番を組み立てています...",
  };

type SignWordsWsServerMessage =
  | { type: "progress"; stage: SignConversionStage }
  | { type: "done"; id: string; text: string; words: SignWord[] }
  | { type: "error"; message: string };

export type SignWordsWsState =
  | { status: "idle" }
  | { status: "connecting" }
  | { status: "progress"; stage: SignConversionStage }
  | { status: "done"; id: string; text: string; words: SignWord[] }
  | { status: "error"; message: string };

const WS_URL = import.meta.env.VITE_SIGN_WORDS_WS_URL;

/** apiFetch と同じ「無ければrefreshTokensしてから再取得」パターン */
const resolveToken = async (): Promise<string | null> => {
  let token = getIdToken();
  if (!token) {
    const refreshed = await refreshTokens();
    if (refreshed) token = getIdToken();
  }
  return token;
};

/**
 * 手話変換WebSocket（進捗push対応）のクライアントフック。
 *
 * 「1接続=1リクエスト」で完結するバックエンド設計（apps/api/src/lambda-sign-words-ws.ts）に
 * 対称な作りにしている。接続が完了/エラーになったらソケットは自然に閉じる
 * （サーバー側が deleteConnection で能動的に切断する）。
 */
export const useSignWordsWs = () => {
  const [state, setState] = useState<SignWordsWsState>({ status: "idle" });
  const socketRef = useRef<WebSocket | null>(null);

  const convert = useCallback(
    async (params: { text: string; kanjiText?: string }) => {
      socketRef.current?.close();
      setState({ status: "connecting" });

      if (!WS_URL) {
        setState({
          status: "error",
          message: "手話変換WebSocketが設定されていません",
        });
        return;
      }

      // ローカル開発（STAGE=local）は認証をバイパスするため、そもそも
      // Cognitoトークンを取りに行かない（dev-sign-words-ws-server.tsも
      // tokenクエリパラメータを見ずモックユーザー固定で動く）。
      const token = isAuthBypassed() ? null : await resolveToken();
      if (!isAuthBypassed() && !token) {
        setState({ status: "error", message: "認証に失敗しました" });
        return;
      }

      const url = token
        ? `${WS_URL}?token=${encodeURIComponent(token)}`
        : WS_URL;
      const socket = new WebSocket(url);
      socketRef.current = socket;

      socket.onopen = () => {
        socket.send(JSON.stringify(params));
      };

      socket.onmessage = (event) => {
        const message = JSON.parse(
          event.data as string,
        ) as SignWordsWsServerMessage;
        if (message.type === "progress") {
          setState({ status: "progress", stage: message.stage });
        } else if (message.type === "done") {
          setState({
            status: "done",
            id: message.id,
            text: message.text,
            words: message.words,
          });
        } else {
          setState({ status: "error", message: message.message });
        }
      };

      socket.onerror = () => {
        setState((s) =>
          s.status === "done" || s.status === "error"
            ? s
            : { status: "error", message: "手話への変換に失敗しました" },
        );
      };

      socket.onclose = () => {
        setState((s) =>
          s.status === "connecting" || s.status === "progress"
            ? { status: "error", message: "接続が切断されました" }
            : s,
        );
      };
    },
    [],
  );

  return { state, convert };
};
